import {
  paymentKindOf,
  type FinancialStatus,
  type FulfilmentStatus,
  type Order,
} from "@estelle/domain";
import { EscalatedLabel } from "@/modules/orders/screens/EscalatedLabel";
import { StickyNote } from "lucide-react";

import { StatusPill } from "@/components/table/StatusPill";
import { cardTime, itemsLabel, money } from "@/modules/orders/format";
import { OrderCommentsQuickView } from "@/modules/orders/screens/OrderCommentsQuickView";
import { OrderQuickView } from "@/modules/orders/screens/OrderQuickView";
import { StageCell } from "@/modules/orders/screens/StageParts";
import {
  HealthLabel,
  StageBadge,
} from "@/modules/orders/screens/TrackingParts";
import { needsAttention } from "@/modules/orders/tracking";

/**
 * The desktop and mobile row pieces `OrdersList` renders through `DataTable`'s
 * override points (#350) — split out from that file so it stays the one file
 * `react-refresh` can fast-refresh as a whole; these are components too, just
 * not the section's default export.
 */

/** `#EL1498`, plus a note icon when the order has one (#350). */
export function OrderNumberCell({ order }: { order: Order }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{order.orderNumber}</span>
      {order.note ? (
        <StickyNote
          aria-label="Has a note"
          className="text-muted-foreground size-3.5 shrink-0"
        />
      ) : null}
    </span>
  );
}

/**
 * The quick-view icons beside the order number (#489, #643): items and
 * tracking always, comments with their count when there are any.
 */
export function OrderQuickIcons({
  order,
  className,
}: {
  order: Order;
  className?: string;
}) {
  return (
    <>
      <OrderQuickView order={order} className={className} />
      <OrderCommentsQuickView order={order} className={className} />
    </>
  );
}

/** "Bengaluru, KA": where the order is going, under the customer's name. */
function placeOf(order: Order): string {
  return [order.shippingAddress?.city, order.shippingAddress?.provinceCode]
    .filter((part): part is string => typeof part === "string" && part !== "")
    .join(", ");
}

/** The customer cell (#643): the name, with where it ships under it. */
export function CustomerCell({ order }: { order: Order }) {
  const name = order.customerName ?? order.shippingAddress?.name ?? "—";
  const place = placeOf(order);
  return (
    <span className="flex min-w-0 flex-col leading-tight">
      <span className="truncate">{name}</span>
      {place ? (
        <span className="text-muted-foreground truncate text-xs font-normal">
          {place}
        </span>
      ) : null}
    </span>
  );
}

/** "COD" or "Prepaid", from the gateway; nothing for a draft nobody paid. */
function paymentWord(order: Order): string | null {
  const kind = paymentKindOf(order.paymentGatewayKey);
  return kind === "cod" ? "COD" : kind === "prepaid" ? "Prepaid" : null;
}

/** The amount cell (#643): the total, with COD or Prepaid under it. */
export function AmountCell({ order }: { order: Order }) {
  const word = paymentWord(order);
  return (
    <span className="flex flex-col items-end leading-tight">
      <span>{money(order.total)}</span>
      {word ? (
        <span className="text-muted-foreground text-xs font-normal">
          {word}
        </span>
      ) : null}
    </span>
  );
}

/** The payment and fulfilment cells: the shared tinted pill (#555) with a
 * dot, so the desktop table and the mobile card (#357) never draw the two
 * differently, and neither differs from the order's own page. */
export function PaymentStatusPill({
  status,
  label,
}: {
  status: FinancialStatus;
  label: string;
}) {
  return <StatusPill dot value={status} label={label} />;
}

export function FulfilmentStatusPill({
  status,
  label,
}: {
  status: "unfulfilled" | FulfilmentStatus;
  label: string;
}) {
  return <StatusPill dot value={status} label={label} />;
}

/**
 * One order on a phone, in three lines (#643): the number with its quick-view
 * icons and the amount; the customer and where it ships; the stage with the
 * Overdue tag. About four and a half cards fitted a screen with the status
 * pills and the delivery method on every card; the stage is the one status,
 * and the rest is a tap away. A parcel that needs a look still says so.
 */
export function OrderMobileCard({
  order,
  showStage = false,
}: {
  order: Order;
  /** The "Tracking stage" column was chosen: draw the stage badge too. */
  showStage?: boolean;
}) {
  const customer = order.customerName ?? order.shippingAddress?.name ?? "—";
  const place = placeOf(order);
  const word = paymentWord(order);

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-0.5">
          <span className="truncate text-sm">{order.orderNumber}</span>
          {order.note ? (
            <StickyNote
              aria-label="Has a note"
              className="text-muted-foreground mx-1 size-3.5 shrink-0"
            />
          ) : null}
          {/* A phone has no hover, so these are the way in (#489). Taller than
              the line they sit on, pulled back with a negative margin so the
              card does not grow to hold a finger-sized target. */}
          <OrderQuickIcons order={order} className="-my-2" />
        </span>
        <span className="shrink-0 text-right text-sm tabular-nums">
          {money(order.total)}
          {word ? (
            <span className="text-muted-foreground ml-1 text-xs font-normal">
              {word}
            </span>
          ) : null}
        </span>
      </div>
      <div className="text-muted-foreground truncate text-sm font-normal">
        {[customer, place || null].filter(Boolean).join(" · ")}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <StageCell
          stage={order.stage}
          overdueHours={order.overdueHours}
          override={order.stageOverride}
        />
        {showStage && order.shipmentStage ? (
          <StageBadge stage={order.shipmentStage} />
        ) : null}
        {needsAttention(order.shipmentHealth) ? (
          <HealthLabel health={order.shipmentHealth} />
        ) : null}
        {order.escalated ? <EscalatedLabel /> : null}
        <span className="text-muted-foreground ml-auto text-xs font-normal">
          {itemsLabel(order.itemCount)} · {cardTime(order.placedAt)}
        </span>
      </div>
    </div>
  );
}
